import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { invalidateAfter, keys } from "../api/query-registry";

export interface Todo {
  id: string;
  title: string;
  homeId: string | null;
  templateId: string | null;
  residentId: string | null;
  dueDate: string | null;
  status: "open" | "done";
  source: "manual" | "template" | "onboarding" | "icp";
  notes: string | null;
  tags: string[];
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface TodoList {
  items: Todo[];
  page: number;
  pageSize: number;
  total: number;
}


export interface CreateTodo {
  title: string;
  dueDate?: string | null;
  homeId?: string | null;
  notes?: string | null;
  tags?: string[];
}

/** Mutations only (create/complete/remove) — the unified queue owns the open
 *  list query, so this avoids a redundant fetch. Invalidates everything under
 *  ["todos"], which covers the queue and the Done view. */
export function useTodoMutations() {
  const qc = useQueryClient();
  const invalidate = () => invalidateAfter(qc, "todoChanged");
  const create = useMutation({
    mutationFn: (p: CreateTodo) => api<Todo>("/api/todos", { method: "POST", body: p }),
    onSuccess: invalidate,
  });
  const complete = useMutation({
    mutationFn: ({ id, done }: { id: string; done: boolean }) =>
      api<Todo>(`/api/todos/${id}/complete`, { method: "POST", body: { done } }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api<void>(`/api/todos/${id}`, { method: "DELETE" }),
    onSuccess: invalidate,
  });
  return { create, complete, remove };
}
/** Fetch every to-do in a status across pages (single-user dataset; the unified
 *  queue needs all open items to bucket correctly, the Done view fetches all
 *  done items on demand). */
export async function fetchAllTodos(status: "open" | "done", homeId?: string, tag?: string): Promise<Todo[]> {
  const out: Todo[] = [];
  let page = 1;
  let total = Infinity;
  while (out.length < total) {
    const qs = new URLSearchParams();
    qs.set("status", status);
    qs.set("page", String(page));
    qs.set("pageSize", "100");
    if (homeId) qs.set("homeId", homeId);
    if (tag) qs.set("tag", tag);
    const res = await api<TodoList>(`/api/todos?${qs.toString()}`);
    out.push(...res.items);
    total = res.total;
    if (res.items.length === 0) break;
    page += 1;
  }
  return out;
}

/** One completed ICP cycle — the resident-level history row in the Done view. */
export interface IcpHistoryItem {
  id: string;
  residentId: string;
  name: string;
  bed: string | null;
  homeId: string;
  homeName: string | null;
  icpDate: string; // YYYY-MM-DD — the staff-written ICP date
  frequencyAfter: string | null;
  createdAt: string;
}

/** Done-view row: a completed to-do or a completed ICP cycle. */
export type DoneItem = { kind: "todo"; todo: Todo } | { kind: "icp"; icp: IcpHistoryItem };

/** Completion date key (YYYY-MM-DD) used to order the merged Done history. */
function doneDateKey(item: DoneItem): string {
  return item.kind === "todo" ? (item.todo.completedAt ?? "").slice(0, 10) : item.icp.icpDate;
}

/** Completed to-dos + completed ICP cycles, most recent first — the Done (history) view. */
export function useDoneTodos(enabled = true) {
  return useQuery({
    queryKey: keys.todosDone(),
    enabled,
    queryFn: async () => {
      const [items, history] = await Promise.all([
        fetchAllTodos("done"),
        api<{ items: IcpHistoryItem[] }>("/api/icp/history"),
      ]);
      const done: DoneItem[] = [
        ...items.map((todo) => ({ kind: "todo" as const, todo })),
        ...history.items.map((icp) => ({ kind: "icp" as const, icp })),
      ];
      return done.sort((a, b) => doneDateKey(b).localeCompare(doneDateKey(a)));
    },
    staleTime: 30_000,
  });
}
